import { Label, LabelSize, LabelWeight, css } from "@sourceful/design-system";
import {
  Currency,
  EngineResult,
  ProductAttribute,
  ProductAttributeValue,
} from "@sourceful/shared-types";
import { formatUnitPrice } from "@sourceful/shared-utils/engine-utils";
import { customReactSelectStyle } from "@styles/customReactSelectStyles";
import Select from "react-select";

const optionStyles = css({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

interface ProductDetailSelectProps {
  attribute: ProductAttribute;
  elementId: string;
  elementName: string;
  labelText: string;
  selectedValue: ProductAttributeValue;
  handleSelect: (selection: ProductAttributeValue) => void;
  pricesByValueId: Record<string, EngineResult>;
  currency?: Currency;
  handleCustomSize?: () => void;
}

type SelectOption = {
  optionType: "option" | "custom-size";
  description?: string;
  label: string;
  value: string;
  pricePerUnitPence: number | null;
};

const ProductDetailSelect = ({
  attribute,
  elementId,
  elementName,
  labelText,
  selectedValue,
  handleSelect,
  pricesByValueId,
  currency = "GBP",
  handleCustomSize,
}: ProductDetailSelectProps) => {
  const { options } = attribute;

  const formattedOptions = options.map<SelectOption>(option => ({
    optionType: "option",
    label: option.label,
    description: option.description,
    value: option.value.id,
    pricePerUnitPence: pricesByValueId[option.value.id]?.price_amount?.price_per_unit_pence ?? null,
  }));

  const selectedOption: SelectOption | null = selectedValue
    ? {
        optionType: "option",
        label: selectedValue.label,
        value: selectedValue.value.id,
        pricePerUnitPence:
          pricesByValueId[selectedValue.value.id]?.price_amount?.price_per_unit_pence ?? null,
      }
    : null;

  return (
    <div>
      <label htmlFor={elementId}>
        <Label
          size={LabelSize.Medium}
          weight={LabelWeight["Semi-Bold"]}
          css={{ marginBottom: "$spacing-fluid-block-xxs" }}
        >
          {labelText}
        </Label>
      </label>

      <Select
        id={`react-select-${elementId}`}
        inputId={elementId}
        name={elementName}
        isSearchable={false}
        options={formattedOptions}
        formatOptionLabel={({ label, description, pricePerUnitPence }: SelectOption, meta) => {
          return (
            <div>
              <div className={optionStyles()}>
                <div>
                  <Label size={LabelSize.Large}>{label}</Label>
                  {description && (
                    <Label
                      size={LabelSize.Small}
                      css={{
                        marginBlockStart: "$spacing-static-block-xxs",
                        fontWeight: "$font-weight-light",
                      }}
                    >
                      {description}
                    </Label>
                  )}
                </div>
                {pricePerUnitPence !== null && meta.context === "menu" && (
                  <Label size={LabelSize.Small} weight={LabelWeight.Regular}>{`${formatUnitPrice(
                    pricePerUnitPence,
                    currency
                  )} per unit`}</Label>
                )}
              </div>
            </div>
          );
        }}
        styles={customReactSelectStyle}
        value={selectedOption}
        onChange={selected => {
          if (!selected) return;
          if (selected.optionType === "custom-size") {
            handleCustomSize && handleCustomSize();
          } else {
            const newValue = options.find(option => option.value.id === selected.value);
            newValue && handleSelect(newValue);
          }
        }}
      />
    </div>
  );
};

export default ProductDetailSelect;
